import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";
import { generateProductsReport } from "../utils/pdfGenerator";

const CATEGORY_COLORS = {
  "Dog Food & Treats": { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-800", icon: "🐶" },
  "Cat Food & Treats": { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-800", dot: "bg-pink-500", badge: "bg-pink-100 text-pink-800", icon: "🐱" },
  "Pet Care":          { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800", icon: "🧴" },
};

const compressImage = (file, maxPx = 600, quality = 0.75) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// Extract a cropped square from an image data URL using the pixelCrop from react-easy-crop
const getCroppedImg = (imageSrc, pixelCrop, outputSize = 400, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        img,
        pixelCrop.x, pixelCrop.y,
        pixelCrop.width, pixelCrop.height,
        0, 0,
        outputSize, outputSize
      );
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = imageSrc;
  });

const AdminProducts = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Add / Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "Dog Food & Treats",
    photoUrl: "",
    variants: [{ label: "", price: 0, photoUrl: "" }]
  });
  const [hoveredVariant, setHoveredVariant] = useState(null);

  // Crop modal state
  const [cropModal, setCropModal] = useState(null); // { imageSrc, variantIndex }
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get("/api/products");
      if (res.data.success) {
        setProducts(res.data.data);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching products:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
      name: "",
      category: "Dog Food & Treats",
      photoUrl: "",
      variants: [{ label: "Standard", price: 0, photoUrl: "" }]
    });
    setModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingId(product._id);
    setFormData({
      name: product.name,
      category: product.category,
      photoUrl: product.photoUrl || "",
      variants: product.variants && product.variants.length > 0
        ? product.variants.map(v => ({ label: v.label, price: v.price, photoUrl: v.photoUrl || "" }))
        : [{ label: "Standard", price: 0, photoUrl: "" }]
    });
    setModalOpen(true);
  };

  const handleAddVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { label: "", price: 0, photoUrl: "" }]
    }));
  };

  const handleRemoveVariant = (index) => {
    if (formData.variants.length <= 1) {
      alert("A product must have at least one variant.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleVariantChange = (index, field, value) => {
    setFormData(prev => {
      const nextVariants = [...prev.variants];
      nextVariants[index] = {
        ...nextVariants[index],
        [field]: field === "price" ? (Number(value) || 0) : value
      };
      return { ...prev, variants: nextVariants };
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setFormData(prev => ({ ...prev, photoUrl: compressed }));
    } catch (err) {
      console.error("Error reading image:", err);
      alert("Failed to process photo");
    }
  };

  const handleVariantPhotoUpload = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Reset crop state and open crop modal
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModal({ imageSrc: ev.target.result, variantIndex: index });
    };
    reader.readAsDataURL(file);
    // Reset file input so the same file can be re-selected
    e.target.value = "";
  };

  const handleCropConfirm = async () => {
    if (!cropModal || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(cropModal.imageSrc, croppedAreaPixels);
      const idx = cropModal.variantIndex;
      setFormData(prev => {
        const nextVariants = [...prev.variants];
        nextVariants[idx] = { ...nextVariants[idx], photoUrl: cropped };
        return { ...prev, variants: nextVariants };
      });
      setCropModal(null);
    } catch (err) {
      console.error("Crop failed:", err);
      alert("Failed to crop image");
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Please enter a product name.");
      return;
    }
    const invalidVariant = formData.variants.some(v => !v.label.trim() || v.price < 0);
    if (invalidVariant) {
      alert("All variants must have a label (e.g. 80g, 200g, Chicken) and a valid price.");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/api/products/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      } else {
        await axios.post("/api/products", formData, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      }
      setModalOpen(false);
      fetchProducts();
    } catch (err) {
      console.error("Error saving product:", err);
      alert(err.response?.data?.message || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Are you sure you want to deactivate "${product.name}"?`)) return;
    try {
      await axios.delete(`/api/products/${product._id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchProducts();
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("Failed to deactivate product");
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm("This will reset all products back to the original default catalog. Continue?")) return;
    setLoading(true);
    try {
      await axios.post("/api/products/seed", {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      await fetchProducts();
    } catch (err) {
      console.error("Error seeding products:", err);
      alert("Failed to reset products");
      setLoading(false);
    }
  };

  // Filter products by category & search query
  const filteredProducts = products.filter(p => {
    const matchCat = selectedCategory === "All" || p.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      p.name.toLowerCase().includes(q) ||
      (p.variants || []).some(v => v.label.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const categories = ["All", "Dog Food & Treats", "Cat Food & Treats", "Pet Care"];

  if (loading) {
    return (
      <div className="text-center py-20 text-[#46735c] font-bold">
        Loading product catalog... 🐾
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Top Header Actions ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-800 flex items-center gap-2">
            🛍️ Products Management
          </h2>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            Manage pet food, treats, and care inventory with size, flavor variants, and photos.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => generateProductsReport(products)}
            className="flex items-center gap-2 px-4 py-2.5 bg-purple-100 text-purple-700 font-bold rounded-2xl hover:bg-purple-200 transition-all border border-purple-200 text-xs sm:text-sm"
          >
            PDF Catalog
          </button>
          <button
            onClick={handleSeedDefaults}
            className="px-4 py-2.5 bg-purple-50 text-purple-600 font-bold rounded-2xl hover:bg-purple-100 transition-all text-xs sm:text-sm border border-purple-100"
          >
            Reset Defaults
          </button>
          <button
            onClick={openAddModal}
            className="px-4 py-2.5 bg-gradient-to-r from-[#46735c] to-[#6992a5] text-white font-bold rounded-2xl shadow-lg hover:scale-105 transition-all text-xs sm:text-sm"
          >
            + Add New Product
          </button>
        </div>
      </div>

      {/* ── Filter & Search Bar ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#f8fafb] p-4 rounded-2xl border border-[#e1ebf0]">
        {/* Category Pills */}
        <div className="flex flex-wrap items-center gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all ${
                selectedCategory === cat
                  ? "bg-[#46735c] text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search input */}
        <div className="relative min-w-[240px]">
          <input
            type="text"
            placeholder="Search products or variants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs sm:text-sm font-medium outline-none focus:border-[#46735c]"
          />
          <span className="absolute left-3 top-2.5 text-xs text-gray-400">🔍</span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-2.5 text-xs text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Products Grid ── */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
          <div className="text-4xl mb-3">📦</div>
          <h3 className="text-base font-bold text-gray-700">No products found</h3>
          <p className="text-xs text-gray-400 mt-1">Try adjusting your category filter or search query.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredProducts.map(product => {
            const catStyle = CATEGORY_COLORS[product.category] || CATEGORY_COLORS["Dog Food & Treats"];
            return (
              <div
                key={product._id}
                className="bg-white border-2 border-gray-100 rounded-3xl p-5 flex flex-col justify-between hover:border-[#6992a5]/40 hover:shadow-md transition-all space-y-4"
              >
                {/* Top: Category Badge */}
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-xl whitespace-nowrap ${catStyle.badge}`}>
                    {catStyle.icon} {product.category}
                  </span>
                </div>

                {/* Middle: Photo + Product Title */}
                <div className="flex items-center gap-3.5">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#f4f7f6] border border-gray-200 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                    {product.photoUrl ? (
                      <img
                        src={product.photoUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl sm:text-3xl">{product.emoji || catStyle.icon || "🐾"}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-black text-gray-800 text-sm sm:text-base leading-tight">
                      {product.name}
                    </h4>
                  </div>
                </div>

                {/* Variants List */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    Sizes / Flavors &amp; Pricing:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(product.variants || []).map((v, i) => (
                      <div
                        key={i}
                        className="relative flex items-center gap-1.5 px-2.5 py-1 bg-[#f8fafb] border border-gray-200 rounded-xl text-xs cursor-default"
                        onMouseEnter={() => v.photoUrl && setHoveredVariant(`${product._id}-${i}`)}
                        onMouseLeave={() => setHoveredVariant(null)}
                      >
                        {v.photoUrl && (
                          <span className="w-4 h-4 rounded overflow-hidden inline-block shrink-0 border border-gray-200">
                            <img src={v.photoUrl} alt={v.label} className="w-full h-full object-cover" />
                          </span>
                        )}
                        <span className="font-semibold text-gray-700">{v.label}</span>
                        <span className="font-black text-[#46735c]">₱{v.price.toLocaleString()}</span>
                        {/* Hover preview */}
                        {v.photoUrl && hoveredVariant === `${product._id}-${i}` && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 pointer-events-none">
                            <img
                              src={v.photoUrl}
                              alt={v.label}
                              className="w-24 h-24 object-cover rounded-xl shadow-xl border-2 border-white ring-1 ring-gray-200"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer with Status & Action Buttons inside the panel */}
                <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[11px] text-gray-400">
                    <span>{product.variants?.length || 0} variant{(product.variants?.length !== 1) ? "s" : ""}</span>
                    <span>•</span>
                    <span className="text-emerald-600 font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Active
                    </span>
                  </div>

                  {/* Edit & Delete Action Buttons inside footer */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => openEditModal(product)}
                      className="p-1.5 sm:p-2 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-xl transition-all border border-purple-200 hover:scale-105"
                      title="Edit product"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product)}
                      className="p-1.5 sm:p-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition-all border border-red-200 hover:scale-105"
                      title="Deactivate product"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add / Edit Product Modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] p-6 sm:p-8 w-full max-w-lg shadow-2xl space-y-5 animate-in zoom-in-95 duration-150 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-lg sm:text-xl font-black text-gray-800">
                {editingId ? "✏️ Edit Product" : "✨ Add New Product"}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="space-y-4">
              {/* Product Name */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Product Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Milky Biscuit, Whiskas"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 outline-none focus:border-[#46735c] focus:bg-white"
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 outline-none focus:border-[#46735c] focus:bg-white"
                >
                  <option value="Dog Food & Treats">🐶 Dog Food &amp; Treats</option>
                  <option value="Cat Food & Treats">🐱 Cat Food &amp; Treats</option>
                  <option value="Pet Care">🧴 Pet Care</option>
                </select>
              </div>


              {/* Variants Builder */}
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Variants (Sizes / Flavors &amp; Price) *
                  </label>
                  <button
                    type="button"
                    onClick={handleAddVariant}
                    className="text-xs font-bold text-[#46735c] hover:underline"
                  >
                    + Add Another Size/Flavor
                  </button>
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {formData.variants.map((v, index) => (
                    <div key={index} className="flex flex-col gap-1.5 p-2.5 bg-gray-50 border border-gray-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        {/* Variant Photo Thumbnail */}
                        <label
                          className="relative shrink-0 w-9 h-9 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#46735c] bg-white cursor-pointer flex items-center justify-center overflow-hidden transition-all group"
                          title="Upload flavor photo"
                        >
                          {v.photoUrl ? (
                            <>
                              <img src={v.photoUrl} alt="variant" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                <span className="text-white text-[9px] font-bold">Change</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-[16px] group-hover:scale-110 transition-transform">📷</span>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleVariantPhotoUpload(e, index)}
                          />
                        </label>

                        <input
                          type="text"
                          placeholder="e.g. 80g or Chicken"
                          value={v.label}
                          onChange={(e) => handleVariantChange(index, "label", e.target.value)}
                          className="flex-1 p-2 bg-white border border-gray-200 rounded-lg text-xs font-bold outline-none focus:border-[#46735c]"
                          required
                        />
                        <div className="flex items-center gap-1 w-28">
                          <span className="text-xs font-bold text-gray-400">₱</span>
                          <input
                            type="number"
                            placeholder="Price"
                            value={v.price}
                            onChange={(e) => handleVariantChange(index, "price", e.target.value)}
                            className="w-full p-2 bg-white border border-gray-200 rounded-lg text-xs font-black text-[#46735c] outline-none focus:border-[#46735c]"
                            min="0"
                            required
                          />
                        </div>
                        {formData.variants.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveVariant(index)}
                            className="p-1 text-red-400 hover:text-red-600 text-sm font-bold shrink-0"
                            title="Remove variant"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {/* Remove variant photo link */}
                      {v.photoUrl && (
                        <button
                          type="button"
                          onClick={() => handleVariantChange(index, "photoUrl", "")}
                          className="self-start text-[10px] text-red-400 hover:text-red-600 font-semibold ml-11"
                        >
                          ✕ Remove flavor photo
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-[#46735c] hover:bg-[#395e4b] text-white font-bold rounded-xl text-sm shadow-md transition-all"
                >
                  {editingId ? "Save Changes" : "Create Product"}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-xl text-sm transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {cropModal && (
        <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          {/* Header */}
          <div className="w-full max-w-sm mb-3 flex items-center justify-between px-1">
            <div>
              <h3 className="text-white font-black text-base">✂️ Crop Flavor Photo</h3>
              <p className="text-gray-400 text-[11px] mt-0.5">Drag to position · Scroll or slide to zoom</p>
            </div>
            <button
              onClick={() => setCropModal(null)}
              className="text-gray-400 hover:text-white text-xl font-bold transition-colors p-1"
            >
              ✕
            </button>
          </div>

          {/* Crop area */}
          <div className="relative w-full max-w-sm" style={{ height: 300 }}>
            <Cropper
              image={cropModal.imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>

          {/* Zoom slider */}
          <div className="w-full max-w-sm mt-4 px-1 flex items-center gap-3">
            <span className="text-gray-400 text-xs">🔍</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full accent-[#46735c] cursor-pointer"
            />
            <span className="text-gray-400 text-xs">{zoom.toFixed(1)}×</span>
          </div>

          {/* Action buttons */}
          <div className="w-full max-w-sm mt-4 flex gap-3">
            <button
              onClick={() => setCropModal(null)}
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleCropConfirm}
              className="flex-1 py-3 bg-[#46735c] hover:bg-[#395e4b] text-white font-bold rounded-xl text-sm shadow-lg transition-all"
            >
              ✓ Use This Crop
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
